import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, Printer, Receipt } from "lucide-react";
import { Modal } from "../Modal";
import { Pairs, RecordSheet, SectionBar } from "../RecordSections";
import { formatSchoolDate } from "../students/studentDisplay";
import { financialService, type StudentOption } from "../../services/financialService";
import { todayIso } from "../../utils/dates";
import { initials, STUDENT_ENROLMENT_DOTS } from "../../pages/financeStudentFilters";
import {
  INVOICE_STANDING_DOTS,
  invoiceStanding,
  STATEMENT_EVENT_LABELS,
  summariseStudentAccount,
} from "./studentAccount";

const ugx = (amount: number) => `UGX ${amount.toLocaleString()}`;
const figure = (amount: string | number) => {
  const value = Number(amount || 0);
  return value ? value.toLocaleString() : <span className="text-gray-300">—</span>;
};

function Dot({ status, dots }: { status: string; dots: Record<string, string> }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium capitalize text-gray-700">
      <span className={`h-1.5 w-1.5 rounded-full ${dots[status] || "bg-gray-400"}`} />
      {status.replace(/_/g, " ")}
    </span>
  );
}

function Tile({ label, value, tone = "text-gray-900", note }: { label: string; value: string; tone?: string; note?: ReactNode }) {
  return (
    <div className="px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-0.5 text-base font-semibold tabular-nums ${tone}`}>{value}</div>
      {note && <div className="text-xs text-gray-500">{note}</div>}
    </div>
  );
}

/** Nothing on a section yet, said in a sentence rather than a zero. */
const Nothing = ({ children }: { children: ReactNode }) => (
  <p className="px-4 py-3 text-gray-500">{children}</p>
);

/** A learner's finance record: where the account stands, what has been
 *  invoiced and when it falls due, the running statement, and every receipt. */
export default function StudentFinancialRecord({
  student,
  canExport,
  onClose,
}: {
  student: StudentOption;
  canExport: boolean;
  onClose: () => void;
}) {
  const today = todayIso();
  const statement = useQuery({
    queryKey: ["student-financial-statement", student.id],
    queryFn: () => financialService.getStudentStatement(student.id),
  });
  const invoices = useQuery({
    queryKey: ["financial-invoices", "student-record", student.id],
    queryFn: () => financialService.getInvoices({ student: student.id, ordering: "-created_at" }),
  });
  const account = summariseStudentAccount(statement.data, invoices.data ?? [], today);
  const loading = statement.isLoading || invoices.isLoading;
  const firstName = student.full_name.split(/\s+/)[0] || "this learner";

  return (
    <Modal
      isOpen
      size="4xl"
      title="Student financial record"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Close
          </button>
          {canExport && account.hasActivity && (
            <>
              <button
                type="button"
                className="btn btn-outline gap-2"
                onClick={() => financialService.downloadStudentStatement(student.id, "csv", { as_of: today })}
              >
                <FileText className="h-4 w-4" /> Statement CSV
              </button>
              <button
                type="button"
                className="btn btn-primary gap-2"
                onClick={() => financialService.downloadStudentStatement(student.id, "pdf", { as_of: today })}
              >
                <Printer className="h-4 w-4" /> Statement PDF
              </button>
            </>
          )}
        </>
      }
    >
      <RecordSheet>
        <header className="flex flex-wrap items-center gap-3 px-4 py-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
            {initials(student.full_name)}
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold leading-6 text-gray-900">{student.full_name}</h2>
            <p className="font-mono text-xs text-gray-500">
              {student.student_id.trim().toUpperCase()}
              {student.admission_number ? ` · ${student.admission_number}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5 text-xs font-medium">
            <span className="inline-flex items-center rounded border border-gray-200 px-2 py-0.5">
              <Dot status={student.status || "active"} dots={STUDENT_ENROLMENT_DOTS} />
            </span>
            <span className="rounded bg-primary-50 px-2 py-0.5 text-primary-700">{student.class_name || "No class"}</span>
            {student.gender && (
              <span className="rounded bg-gray-100 px-2 py-0.5 capitalize text-gray-700">{student.gender}</span>
            )}
          </div>
        </header>

        {loading ? (
          <div className="space-y-2 border-t border-gray-200 px-4 py-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="h-6 animate-pulse rounded bg-gray-100" />
            ))}
          </div>
        ) : statement.isError ? (
          <div className="border-t border-gray-200 px-4 py-4 text-red-700">
            This learner's account could not be loaded.{" "}
            <button type="button" className="font-medium underline" onClick={() => statement.refetch()}>
              Try again
            </button>
          </div>
        ) : !account.hasActivity ? (
          <div className="flex flex-col items-center gap-2 border-t border-gray-200 px-6 py-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <Receipt className="h-6 w-6" />
            </span>
            <h3 className="text-sm font-semibold text-gray-900">No fees billed to {firstName} yet</h3>
            <p className="max-w-md text-gray-500">
              The account opens with the first invoice — usually when a fee structure for{" "}
              {student.class_name || "their class"} is billed. Payments, credit notes and refunds will
              appear here as they are recorded.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 divide-x divide-y divide-gray-200 border-t border-gray-200 sm:grid-cols-4 sm:divide-y-0">
              <Tile
                label="Billed"
                value={ugx(account.billed)}
                note={account.discounts > 0 && `Less discounts ${ugx(account.discounts)}`}
              />
              <Tile label="Paid" value={ugx(account.paid)} tone="text-green-700" note={account.refunded > 0 && `After refunds of ${ugx(account.refunded)}`} />
              <Tile
                label="Outstanding"
                value={ugx(account.outstanding)}
                tone={account.outstanding > 0 ? "text-red-600" : "text-green-700"}
                note={account.overdueCount > 0 && <span className="text-red-600">{ugx(account.overdueAmount)} overdue</span>}
              />
              <Tile
                label="Credit held"
                value={ugx(account.creditHeld)}
                tone={account.creditHeld > 0 ? "text-primary-700" : "text-gray-400"}
                note={account.creditHeld > 0 && account.balanceAfterCredit !== null && `Leaves ${ugx(account.balanceAfterCredit)} to pay`}
              />
            </div>

            <SectionBar letter="A" title="Account" />
            <Pairs
              rows={[
                ["Invoices", account.invoiceCount ? `${account.invoiceCount} issued · ${account.openCount} open` : "None issued"],
                [
                  "Overdue",
                  account.overdueCount
                    ? <span className="text-red-600">{ugx(account.overdueAmount)} on {account.overdueCount} invoice{account.overdueCount === 1 ? "" : "s"}</span>
                    : "Nothing overdue",
                ],
                [
                  "Next due",
                  account.nextDue &&
                    `${formatSchoolDate(account.nextDue.due_date)} · ${ugx(Number(account.nextDue.balance_due))} on ${account.nextDue.invoice_number}`,
                ],
                [
                  "Last payment",
                  account.lastReceipt &&
                    `${formatSchoolDate(account.lastReceipt.date)} · ${ugx(Number(account.lastReceipt.amount))} by ${account.lastReceipt.method}`,
                ],
                ["Discounts given", account.discounts > 0 && ugx(account.discounts)],
                ["Credit notes", account.credited > 0 && ugx(account.credited)],
              ]}
            />

            <SectionBar letter="B" title="Invoices" />
            {invoices.data?.length ? (
              <div className="max-h-64 overflow-auto">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th className="sheet-rownum">#</th>
                      <th>Invoice</th>
                      <th>Fee structure</th>
                      <th>Due</th>
                      <th className="text-right">Total</th>
                      <th className="text-right">Paid</th>
                      <th className="text-right">Credited</th>
                      <th className="text-right">Balance</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.data.map((row, index) => {
                      const standing = invoiceStanding(row, today);
                      return (
                        <tr key={row.id}>
                          <td className="sheet-rownum">{index + 1}</td>
                          <td className="font-mono text-xs text-gray-900">{row.invoice_number}</td>
                          <td className="max-w-[14rem] truncate text-gray-600" title={row.fee_structure_name || row.notes || ""}>
                            {row.fee_structure_name || row.notes || <span className="text-gray-300">—</span>}
                          </td>
                          <td className={`tabular-nums text-xs ${standing === "overdue" ? "text-red-600" : "text-gray-600"}`}>
                            {formatSchoolDate(row.due_date)}
                          </td>
                          <td className="text-right tabular-nums">{figure(row.total_amount)}</td>
                          <td className="text-right tabular-nums text-green-700">{figure(row.paid_amount)}</td>
                          <td className="text-right tabular-nums">{figure(row.credited_amount)}</td>
                          <td className="text-right font-semibold tabular-nums">{figure(row.balance_due)}</td>
                          <td>
                            <Dot status={standing} dots={INVOICE_STANDING_DOTS} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <Nothing>No invoices have been issued.</Nothing>
            )}

            <SectionBar letter="C" title="Statement of account" />
            {statement.data?.events.length ? (
              <div className="max-h-72 overflow-auto">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Reference</th>
                      <th>Description</th>
                      <th className="text-right">Debit</th>
                      <th className="text-right">Credit</th>
                      <th className="text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statement.data.events.map((event, index) => (
                      <tr key={index}>
                        <td className="tabular-nums text-xs text-gray-600">{formatSchoolDate(event.date)}</td>
                        <td className="text-gray-700">{STATEMENT_EVENT_LABELS[event.type] ?? event.type}</td>
                        <td className="font-mono text-xs text-gray-600">{event.reference}</td>
                        <td className="max-w-[18rem] truncate text-gray-700" title={event.description}>
                          {event.description}
                        </td>
                        <td className="text-right tabular-nums">{figure(event.debit)}</td>
                        <td className="text-right tabular-nums text-green-700">{figure(event.credit)}</td>
                        <td className="text-right font-medium tabular-nums">{Number(event.balance).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 font-semibold">
                      <td colSpan={6} className="text-right">Outstanding balance</td>
                      <td className={`text-right tabular-nums ${account.outstanding > 0 ? "text-red-600" : "text-green-700"}`}>
                        {account.outstanding.toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <Nothing>Nothing has been charged or settled against an invoice yet.</Nothing>
            )}

            <SectionBar letter="D" title="Payments received" />
            {statement.data?.receipts?.length ? (
              <div className="max-h-64 overflow-auto">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th className="sheet-rownum">#</th>
                      <th>Date</th>
                      <th>Receipt</th>
                      <th>Method</th>
                      <th className="text-right">Amount</th>
                      <th className="text-right">Applied</th>
                      <th className="text-right">Held as credit</th>
                      <th className="text-right">Paid directly</th>
                      <th className="text-right">Refunded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statement.data.receipts.map((receipt, index) => (
                      <tr key={`${receipt.receipt_number}-${index}`}>
                        <td className="sheet-rownum">{index + 1}</td>
                        <td className="tabular-nums text-xs text-gray-600">{formatSchoolDate(receipt.date)}</td>
                        <td className="font-mono text-xs text-gray-900" title={receipt.reference || undefined}>
                          {receipt.receipt_number || receipt.reference || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="text-gray-700">{receipt.method}</td>
                        <td className="text-right font-semibold tabular-nums">{figure(receipt.amount)}</td>
                        <td className="text-right tabular-nums">{figure(receipt.applied)}</td>
                        <td className="text-right tabular-nums">{figure(receipt.held_as_credit)}</td>
                        <td className="text-right tabular-nums">{figure(receipt.paid_directly)}</td>
                        <td className="text-right tabular-nums text-red-600">{figure(receipt.refunded)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Nothing>No payments have been received from {firstName}'s family.</Nothing>
            )}
          </>
        )}
      </RecordSheet>
    </Modal>
  );
}
